import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import { EditorView } from "@tiptap/pm/view";
import { Editor } from "@tiptap/react";
import { Dispatch, SetStateAction } from "react";

/**
 * Adds a file as an attachment
 */
const addFileAsAttachment = (
    file: File,
    setAttachments: Dispatch<SetStateAction<EmailAttachment[]>>,
): void => {
    const fileReader = new FileReader();

    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
        const readerResult = fileReader.result as string;
        const removedDataUrlPrefix = readerResult.replace(
            /^data:.*?;base64,/,
            "",
        );

        setAttachments((previousAttachments) => [
            ...previousAttachments,
            new EmailAttachment({
                id: crypto.randomUUID(),
                name: file.name,
                size: file.size,
                type: file.type,
                path: removedDataUrlPrefix,
                preview: readerResult,
                createdAt: new Date(),
                modifiedAt: new Date(),
            }),
        ]);
    };
};

/**
 * Handles multiple files with an editor
 */
export const handleFilesWithEditor = (
    files: File[],
    editor: Editor,
    position: number,
    setAttachments?: Dispatch<SetStateAction<EmailAttachment[]>>,
): void => {
    files.forEach((file) => {
        if (file.type.includes("image")) {
            const fileReader = new FileReader();

            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                editor
                    .chain()
                    .insertContentAt(position, {
                        type: "image",
                        attrs: {
                            src: fileReader.result,
                        },
                    })
                    .focus()
                    .run();
            };
        } else if (setAttachments) {
            addFileAsAttachment(file, setAttachments);
        }
    });
};

/**
 * Handles multiple files with a ProseMirror view
 */
export const handleFilesWithView = (
    files: File[],
    view: EditorView,
    position: number,
    setAttachments?: Dispatch<SetStateAction<EmailAttachment[]>>,
): void => {
    files.forEach((file) => {
        if (file.type.includes("image")) {
            const fileReader = new FileReader();

            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                const { state, dispatch } = view;
                const node = state.schema.nodes.image.create({
                    src: fileReader.result,
                });
                const transaction = state.tr.insert(position, node);

                dispatch(transaction);
                view.focus();
            };
        } else if (setAttachments) {
            addFileAsAttachment(file, setAttachments);
        }
    });
};
